import { Props as ReactApexChartProps } from 'react-apexcharts';
import { useMemo } from 'react';
import { ChartProps } from '../../dashboard-components/dashboard-charts/use-dashboard-charts';
import { useLang } from '../../../../../../hooks/use-lang';
import { useQueryParams } from '../../../../../../hooks/use-query-params';
import dayjs from 'dayjs';
import { useDashboardStore } from '../../../../../../store/use-dashboard-store';

interface DataType {
  time: string[];
  values: {
    name: string;
    data: number[];
  }[];
}

export const useOrdersHeatmap = (): ChartProps => {
  const params = useQueryParams().getQueryParams();
  const range = params.range ? JSON.parse(params.range) as { from: string; to: string } : undefined;
  const from = range?.from;
  const to = range?.to;

  const { t, currentLang } = useLang();

  const { list } = useDashboardStore();
  const data = list?.menu_heat_map as DataType;

  const chartData: ReactApexChartProps = useMemo(() => {
    const fromDay = dayjs(from);
    const toDay = dayjs(to);
    const days: number[] = [];
    for (let i = fromDay; i <= toDay; i = i.add(1, 'day')) {
      const day = i.isoWeekday() - 1;
      days[day] = (days[day] || 0) + 1;
    }

    const categories = data?.time;
    const isValuesExist = data?.values.some((item) => item.data.some((value) => value > 0));
    const series = data?.values.map((item, index) => ({
      ...item,
      name: `${t(item.name)} (${days[index] || 0})`,
    }));

    series?.reverse();

    return {
      series: isValuesExist ? series : [],
      options: {
        chart: {
          type: 'heatmap',
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          heatmap: {
            radius: 8,
            colorScale: {
              ranges: [
                { from: 0, to: 0, color: '#FFE2D8', foreColor: '#000' },
                { from: 1, to: 10, color: '#FEA57B', foreColor: '#000' },
                { from: 11, to: 20, color: '#FF9D8C' },
                { from: 21, to: 30, color: '#FF593C' },
                { from: 31, to: 40, color: '#8B050C' },
              ],
            },
          },
        },
        xaxis: {
          categories,
          axisTicks: {
            show: false,
          },
          axisBorder: {
            show: false,
          },
        },
        yaxis: {
          labels: {
            align: 'left',
            offsetX: 10,
          },
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left',
          offsetX: -34,
          markers: {
            fillColors: ['#FFE2D8', '#FEA57B', '#FF9D8C', '#FF593C', '#8B050C'],
          },
        },
        grid: {
          show: false,
        },
        stroke: {
          width: 6,
        },
      },
    };
  }, [data, currentLang, from, to]);

  return {
    series: chartData.series,
    options: chartData.options,
    label: 'Тепловая карта',
    subLabel:
      'Тепловая карта наглядно отражает активность заказов по дням и времени, показывая периоды максимального спроса. В скобках указано кол-во активных дней за период',
    type: 'heatmap',
  };
};
